import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { LightCard } from 'components/Card'
import Column from 'components/Column'
import Loader from 'components/Loader'
import QuestionHelper from 'components/QuestionHelper'
import { RowBetween } from 'components/Row'
import Toggle from 'components/Toggle'
import { useActualFeePct } from 'hooks/useActualBaultFeePct'
import React, { useMemo } from 'react'
import { Zap } from 'react-feather'
import { Text } from 'rebass'
import styled from 'styled-components'
import { Address } from 'viem'

import { formatPercentage } from '../../../../../utils/numbers'

const ToggleWrapper = styled(LightCard)<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: border-color 0.2s ease-in-out;
  border-color: ${({ theme }) => theme.cardBorderSecondary};
  padding: 16px;
`

const ToggleLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const ReceiveCard = styled(LightCard)`
  padding: 12px;
  border-radius: 8px;
  margin-top: 12px;
`

const TokenRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TokenIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.deprecated_blue1};
  display: flex;
  align-items: center;
  justify-content: center;
`

const APRSection = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(75, 85, 99, 0.3);
  gap: 1rem;
`

const APRRow = styled(RowBetween)`
  margin-bottom: 4px;
`

const ApyValue = styled.div`
  display: flex;
  align-items: center;
`

interface AutoCompoundDetsProps {
  isActive: boolean
  onToggle: () => void
  baultAddress?: Address
  currency?: Currency | null
  expectedShares?: CurrencyAmount<Currency> | undefined
  isLoading?: boolean
  vaultSymbol?: string
  apy?: string
  helperText?: string
  rewardVaultRewardSymbol?: string
}

const AutoCompoundDets: React.FC<AutoCompoundDetsProps> = ({
  isActive,
  onToggle,
  currency,
  baultAddress,
  expectedShares,
  isLoading = false,
  vaultSymbol,
  apy,
  helperText,
  rewardVaultRewardSymbol,
}) => {
  const { feePct: actualFeePct } = useActualFeePct(baultAddress, expectedShares as CurrencyAmount<Token>)

  const fee = useMemo(() => {
    if (!actualFeePct) return 0
    return Math.floor(actualFeePct * 100) / 100
  }, [actualFeePct])
  const rewardVaultLabel = rewardVaultRewardSymbol ? `${rewardVaultRewardSymbol} Reward Vault` : 'Reward Vault'

  const defaultHelperText = isActive
    ? 'Auto-compounding automatically reinvests your rewards to grow your position through compound interest, saving you gas fees and maximizing returns.'
    : `When disabled, your LP tokens will be staked in the ${rewardVaultLabel}, earning rewards that can be manually claimed but will not automatically compound.`

  const finalHelperText = helperText || defaultHelperText

  return (
    <>
      <ToggleWrapper active={isActive}>
        <ToggleLabel>
          <Zap size={16} color={isActive ? '#3b82f6' : '#6b7280'} />
          <Text fontSize={14} fontWeight={500}>
            Auto-Compound Rewards
          </Text>
          <QuestionHelper text={finalHelperText} />
        </ToggleLabel>
        <Toggle isActive={isActive} toggle={onToggle} size={24} />
      </ToggleWrapper>

      {isActive && (
        <ReceiveCard>
          <Text fontSize={14} color="#9ca3af" mb={2}>
            You will receive:
          </Text>

          <RowBetween mb={2}>
            <TokenRow>
              <TokenIcon>
                <Zap size={14} color="#93c5fd" />
              </TokenIcon>
              <Text fontSize={14} fontWeight={500}>
                {/* {vaultSymbol || `bault Shares`} */}
                Pool Tokens
              </Text>
            </TokenRow>

            {isLoading ? (
              <Loader size="16px" />
            ) : (
              <Text fontSize={16} fontWeight={500}>
                ~{expectedShares?.toSignificant(4) || '0.00'}
              </Text>
            )}
          </RowBetween>

          <Text fontSize={12} color="#9ca3af">
            These shares represent your position in the auto-compounding vault (Bault).
          </Text>

          <APRSection>
            <Text fontSize={12} color="#9ca3af">
              With Auto-Compound
            </Text>
            <Column gap="0.5rem">
              <APRRow>
                <Text fontSize={12} color="#9ca3af">
                  Deposit Fee:
                </Text>
                <ApyValue>
                  <Text fontSize={12} color="#9ca3af">
                    0.00%
                  </Text>
                </ApyValue>
              </APRRow>
              <APRRow>
                <Text fontSize={12} color="#9ca3af">
                  Exit Fee:
                </Text>
                <Text fontSize={12} color="#9ca3af">
                  {formatPercentage(fee.toString())}
                </Text>
              </APRRow>
            </Column>
          </APRSection>
        </ReceiveCard>
      )}
    </>
  )
}

export default React.memo(AutoCompoundDets)
